import {
  SetCartAction,
  currentCartsChangedOutsideOfApp,
  giftCardRemovedBecauseCartChanged,
  setCart
} from '@spa/lib/store/carts/cartsSlice';
import { actionChannel, put, race, select, take } from 'typed-redux-saga';
import { OptionalStateCarts } from '@spa/lib/store/carts/CartsState';
import { checkoutOnePagePath } from '@spa/lib/const';
import createBroadcastEventChannel from '@spa/lib/store/sagas/createBroadcastEventChannel';
import { giftCardReconciledAfterCartEntryChange } from '@spa/lib/store/carts/cartCrossTabEvents';
import { hydrateType } from '@spa/lib/store/HydrateAction';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectPathname from '@spa/lib/store/router/selectors/selectPathname';

interface GiftCardReconciledAfterCartEntryChangeEvent {
  readonly carts: OptionalStateCarts;
  readonly type: 'giftCardReconciledAfterCartEntryChange';
}

function* watchAndNotifyCurrentCartChanges(): Generator {
  let broadcastChannel: BroadcastChannel | undefined;
  if (BroadcastChannel !== undefined) {
    broadcastChannel = new BroadcastChannel('mul-carts');
  }
  let cartEventsBroadcastChannel: BroadcastChannel | undefined;
  if (BroadcastChannel !== undefined) {
    cartEventsBroadcastChannel = new BroadcastChannel('mul-cart-events');
  }

  const broadcastEventChannel = createBroadcastEventChannel<OptionalStateCarts>(broadcastChannel);
  const cartEventsChannel =
    createBroadcastEventChannel<GiftCardReconciledAfterCartEntryChangeEvent>(
      cartEventsBroadcastChannel
    );

  const requestChan = yield* actionChannel(['*']);
  let lastSessionCarts: OptionalStateCarts | null = null;
  while (true) {
    const { action, broadcast, cartEvent } = yield* race({
      action: take(requestChan),
      broadcast: take(broadcastEventChannel),
      cartEvent: take(cartEventsChannel)
    });
    if (action !== undefined && giftCardReconciledAfterCartEntryChange.match(action)) {
      const carts: OptionalStateCarts = yield* select(selectCarts);
      cartEventsBroadcastChannel?.postMessage({
        carts,
        type: 'giftCardReconciledAfterCartEntryChange'
      } satisfies GiftCardReconciledAfterCartEntryChangeEvent);
      lastSessionCarts = carts;
    } else if (
      action !== undefined &&
      ((typeof action.type === 'string' && action.type.startsWith('carts/')) ||
        action.type === hydrateType)
    ) {
      const carts: OptionalStateCarts = yield* select(selectCarts);
      if (lastSessionCarts !== null && lastSessionCarts !== carts) {
        let propagateChanges: boolean = true;
        if (setCart.match(action)) {
          const setCartAction: SetCartAction = action;
          propagateChanges = setCartAction.payload.localChange;
        } else if (currentCartsChangedOutsideOfApp.match(action)) {
          propagateChanges = false;
        }

        if (propagateChanges) {
          broadcastChannel?.postMessage(carts);
        }
      }
      lastSessionCarts = carts;
    } else if (broadcast !== undefined) {
      yield* put(
        currentCartsChangedOutsideOfApp({
          carts: broadcast
        })
      );
    } else if (cartEvent !== undefined) {
      yield* put(
        currentCartsChangedOutsideOfApp({
          carts: cartEvent.carts
        })
      );
      const pathname: string = yield* select(selectPathname);
      if (pathname.startsWith(checkoutOnePagePath)) {
        yield* put(giftCardRemovedBecauseCartChanged());
      }
    }
  }
}

export default watchAndNotifyCurrentCartChanges;
